import { useCallback, useRef, useEffect } from 'react';
import { SOUND_EFFECTS, SOUND_EFFECT_ENUM } from '@/constants';
import { useSoundStore } from '@/stores/sound/soundStore';

export const useSoundEffects = (config = {}) => {
  const audioRefs = useRef(new Map());
  const customAudioRefs = useRef(new Map());

  // Global sound settings from store (reactive)
  const enabled = useSoundStore((s) => s.enabled);
  const tooltipEnabled = useSoundStore((s) => s.tooltipEnabled);
  const messageSendEnabled = useSoundStore((s) => s.messageSendEnabled);
  const liveAgentMessageEnabled = useSoundStore((s) => s.liveAgentMessageEnabled);
  const volume = useSoundStore((s) => s.volume);
  const setSettings = useSoundStore((s) => s.setSettings);

  // Extract customization data from config
  const { customizationData } = config;
  useEffect(() => {
    const initializeAudio = async () => {
      const audioMap = new Map();
      const defaultSoundOption = SOUND_EFFECTS.BUBBLE_SOUND_OPTIONS[0]; // Use first option as default
      
      if (defaultSoundOption) {
        // Create default audio instances for fallback
        const tooltipAudio = new Audio(defaultSoundOption.file);
        tooltipAudio.volume = volume;
        tooltipAudio.preload = 'auto';
        tooltipAudio.addEventListener('error', () => {
          console.warn(`Failed to load default tooltip sound: ${defaultSoundOption.file}`);
        });
        audioMap.set('tooltip_show', tooltipAudio);

        const messageSendAudio = new Audio(defaultSoundOption.file);
        messageSendAudio.volume = volume;
        messageSendAudio.preload = 'auto';
        messageSendAudio.addEventListener('error', () => {
          console.warn(`Failed to load default message send sound: ${defaultSoundOption.file}`);
        });
        audioMap.set('message_send', messageSendAudio);
      }
      
      audioRefs.current = audioMap;
    };
    initializeAudio();
  }, []);

  // Initialize custom sound based on customizationData
  useEffect(() => {
    if (customizationData?.sound_enabled && customizationData?.sound_id) {
      const soundOption = SOUND_EFFECTS.BUBBLE_SOUND_OPTIONS.find(
        option => option.id === customizationData.sound_id
      );
      
      if (soundOption) {
        // Create one audio instance for all sound types
        const customAudio = new Audio(soundOption.file);
        customAudio.volume = volume;
        customAudio.preload = 'auto';
        customAudio.addEventListener('error', () => {
          console.warn(`Failed to load custom sound: ${soundOption.file}`);
        });
        customAudioRefs.current.set('custom_sound', customAudio);
      }
    } else {
      // Clear custom sound if disabled or no sound selected
      customAudioRefs.current.delete('custom_sound');
    }
  }, [customizationData?.sound_enabled, customizationData?.sound_id, volume]);

  // Keep audio volume in sync with store
  useEffect(() => {
    audioRefs.current.forEach((audio) => {
      audio.volume = volume;
    });
    customAudioRefs.current.forEach((audio) => {
      audio.volume = volume;
    });
  }, [volume]);

  const playSound = useCallback(async (soundType) => {
    if (!enabled) return;

    const soundEnabledMap = {
      [SOUND_EFFECT_ENUM.TOOLTIP_SHOW]: tooltipEnabled && customizationData?.sound_enabled,
      [SOUND_EFFECT_ENUM.MESSAGE_SEND]: messageSendEnabled && customizationData?.sound_enabled,
      [SOUND_EFFECT_ENUM.LIVE_AGENT_MESSAGE]: liveAgentMessageEnabled && customizationData?.sound_enabled,
    };
    if (!soundEnabledMap[soundType]) return;

    try {
      let audio;
      
      // Use custom sound for all sound types if available
      if (customizationData?.sound_enabled && customizationData?.sound_id) {
        audio = customAudioRefs.current.get('custom_sound');
      }
      
      // Fallback to default sounds if no custom sound available
      if (!audio) {
        const soundKeyMap = {
          [SOUND_EFFECT_ENUM.TOOLTIP_SHOW]: 'tooltip_show',
          [SOUND_EFFECT_ENUM.MESSAGE_SEND]: 'message_send',
        };
        const audioKey = soundKeyMap[soundType];
        audio = audioRefs.current.get(audioKey);
      }
      
      if (audio) {
        audio.currentTime = 0;
        await audio.play();
      }
    } catch (error) {
      console.warn('Failed to play sound effect:', error);
    }
  }, [enabled, tooltipEnabled, messageSendEnabled, liveAgentMessageEnabled, customizationData?.sound_enabled, customizationData?.sound_id]);

  const playTooltipSound = useCallback(() => {
    playSound(SOUND_EFFECT_ENUM.TOOLTIP_SHOW);
  }, [playSound]);

  const playMessageSendSound = useCallback(() => {
    playSound(SOUND_EFFECT_ENUM.MESSAGE_SEND);
  }, [playSound]);

  const playLiveAgentMessageSound = useCallback(() => {
    playSound(SOUND_EFFECT_ENUM.LIVE_AGENT_MESSAGE);
  }, [playSound]);

  // Update settings via store so UI re-renders
  const updateSoundConfig = useCallback((newConfig) => {
    setSettings((prev) => ({ ...(typeof prev === 'object' ? prev : {}), ...newConfig }));
  }, [setSettings]);

  return {
    playSound,
    playTooltipSound,
    playMessageSendSound,
    playLiveAgentMessageSound,
    updateSoundConfig,
    soundConfig: { enabled, tooltipEnabled, messageSendEnabled, liveAgentMessageEnabled, volume }
  };
};
